import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import ToggleSwitch from '.././ToggleSwitch';
import type { ICustomAlertPreference } from '../../types/api-caller';
import Loading from '.././Loading';
import useApiCaller from '../../hooks/use-api-caller';
import { toast } from 'react-toastify';
import CustomAlertEditor from './CustomAlertEditor';
import CustomAlertSummaryCard from './CustomAlertSummaryCard';
import { sortCustomAlertsByName } from '../../utils/custom-alerts';

export interface CustomAlertsSettingsHandle {
  buildPayload: () => ICustomAlertPreference[];
  forceDisableAll: () => void;
}

interface CustomAlertsSettingsProps {
  initialPreferences: ICustomAlertPreference[];
  alertsEnabled: boolean;
  onDirty: () => void;
  onCustomEnabledChange: (enabled: boolean) => void;
}

const ALERT_CARD_SELECTOR = '[data-custom-alert-card="true"]';

function getNextGeneratedAlertName(alerts: ICustomAlertPreference[]): string {
  const alertNamePattern = /^Alert\s+(\d+)$/i;
  let highestAlertNumber = 0;

  alerts.forEach((alert) => {
    const match = alert.name?.trim().match(alertNamePattern);
    if (!match) return;

    const parsedAlertNumber = Number(match[1]);
    if (Number.isInteger(parsedAlertNumber) && parsedAlertNumber > highestAlertNumber) {
      highestAlertNumber = parsedAlertNumber;
    }
  });

  return `Alert ${highestAlertNumber + 1}`;
}

export function createDefaultCustomAlertPayload(alertName: string): ICustomAlertPreference {
  return {
    name: alertName,
    alert_id: 0,
    enabled: true,
    increase_threshold: 10,
    decrease_threshold: null,
    frequency: 'DAILY',
    scope_type: 'PORTFOLIO',
    exclude_low_articles: false,
    list_ids: [],
  };
}

const CustomAlertsSettings = forwardRef<CustomAlertsSettingsHandle, CustomAlertsSettingsProps>(
  ({ initialPreferences, alertsEnabled, onDirty, onCustomEnabledChange }, ref) => {
    CustomAlertsSettings.displayName = 'CustomAlertsSettings';

    const apiCaller = useApiCaller();

    const [alerts, setAlerts] = useState<ICustomAlertPreference[]>([]);
    const [customAlertsEnabled, setCustomAlertsEnabled] = useState<boolean>(false);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [editingAlertId, setEditingAlertId] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // Click outside to collapse
    useEffect(() => {
      if (editingAlertId === null) return;

      function handleClickOutside(e: MouseEvent) {
        const target = e.target;

        // Keep editing state when interacting with any alert card.
        if (target instanceof Element && target.closest(ALERT_CARD_SELECTOR)) {
          return;
        }

        setEditingAlertId(null);
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [editingAlertId]);

    useEffect(() => {
      setLoading(true);

      if (!initialPreferences || initialPreferences.length === 0) {
        setAlerts([]);
        setCustomAlertsEnabled(false);
        setLoading(false);
        return;
      }

      setAlerts([...initialPreferences]);
      setCustomAlertsEnabled(initialPreferences.some((a) => a.enabled));
      setLoading(false);
    }, []);

    const sortedAlerts = sortCustomAlertsByName(alerts);
    const createDefaultName = getNextGeneratedAlertName(alerts);

    async function refetchAlerts(): Promise<ICustomAlertPreference[]> {
      try {
        const response = await apiCaller.getEmailPreferences();
        if (response.custom && response.custom.length > 0) {
          setAlerts(response.custom);
          return response.custom;
        } else {
          setAlerts([]);
          return [];
        }
      } catch {
        toast.error('Failed to refresh alerts');
        return [];
      }
    }

    async function handleCustomAlertsEnabledChange(checked: boolean) {
      setCustomAlertsEnabled(checked);
      onCustomEnabledChange(checked);

      if (checked && alerts.length === 0) {
        const defaultAlertPayload = createDefaultCustomAlertPayload(createDefaultName);
        setIsCreating(false);
        setEditingAlertId(null);

        try {
          const createdAlert = await apiCaller.createCustomAlert(defaultAlertPayload);
          const refreshedAlerts = await refetchAlerts();
          const createdAlertId = createdAlert?.alert_id;
          const createdAlertMatch =
            (typeof createdAlertId === 'number' && refreshedAlerts.find((a) => a.alert_id === createdAlertId)) ||
            refreshedAlerts.find((a) => a.name === defaultAlertPayload.name);

          if (createdAlertMatch?.alert_id != null) {
            setEditingAlertId(createdAlertMatch.alert_id);
          }
        } catch {
          toast.error('Failed to update alerts');
        }

        return;
      }

      const updatedAlerts = alerts.map((a) => ({ ...a, enabled: checked }));
      setAlerts(updatedAlerts);

      try {
        await Promise.all(updatedAlerts.map((a) => apiCaller.updateCustomAlert(a)));
        await refetchAlerts();
      } catch {
        toast.error('Failed to update alerts');
      }
    }

    async function handleCreateAlert(payload: ICustomAlertPreference) {
      try {
        await apiCaller.createCustomAlert(payload);
        await refetchAlerts();
        setIsCreating(false);
        setCustomAlertsEnabled(true);
        onCustomEnabledChange(true);
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 400) {
          toast.error('An alert with this name already exists');
        } else {
          toast.error('Failed to create alert');
        }
      }
    }

    async function handleUpdateAlert(updatedAlert: ICustomAlertPreference) {
      try {
        await apiCaller.updateCustomAlert(updatedAlert);
        setAlerts((prev) => prev.map((a) => (a.alert_id === updatedAlert.alert_id ? updatedAlert : a)));
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 400) {
          toast.error('An alert with this name already exists');
        } else if (status === 404) {
          toast.error('Alert not found — it may have been deleted');
          await refetchAlerts();
        } else {
          toast.error('Failed to update alert');
        }
      }
    }

    async function handleDeleteAlert(alertId: number) {
      try {
        await apiCaller.deleteCustomAlert({ alert_id: alertId });
        setAlerts((prev) => {
          const remaining = prev.filter((a) => a.alert_id !== alertId);
          if (remaining.length === 0) {
            setCustomAlertsEnabled(false);
            onCustomEnabledChange(false);
          }
          return remaining;
        });
        if (editingAlertId === alertId) {
          setEditingAlertId(null);
        }
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 404) {
          toast.error('Alert not found — it may have already been deleted');
          await refetchAlerts();
        } else {
          toast.error('Failed to delete alert');
        }
      }
    }

    async function handleToggleAlert(alertId: number, enabled: boolean) {
      const alert = alerts.find((a) => a.alert_id === alertId);
      if (!alert) return;

      const updated = { ...alert, enabled };
      try {
        await apiCaller.updateCustomAlert(updated);
        setAlerts((prev) => prev.map((a) => (a.alert_id === alertId ? updated : a)));

        const allAlerts = alerts.map((a) => (a.alert_id === alertId ? updated : a));
        const anyEnabled = allAlerts.some((a) => a.enabled);
        setCustomAlertsEnabled(anyEnabled);
        onCustomEnabledChange(anyEnabled);
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 404) {
          toast.error('Alert not found — it may have been deleted');
          await refetchAlerts();
          return;
        }
        toast.error('Failed to update alert');
      }
    }

    function handleEditAlert(alertId: number) {
      setEditingAlertId((prev) => (prev === alertId ? null : alertId));
      setIsCreating(false);
    }

    useImperativeHandle(ref, () => ({
      buildPayload(): ICustomAlertPreference[] {
        return alerts.map((a) => ({
          ...a,
          enabled: alertsEnabled && customAlertsEnabled ? a.enabled : false,
        }));
      },

      async forceDisableAll() {
        const updatedAlerts = alerts.map((a) => ({ ...a, enabled: false }));
        setAlerts(updatedAlerts);
        setCustomAlertsEnabled(false);
        onCustomEnabledChange(false);

        try {
          await Promise.all(updatedAlerts.map((a) => apiCaller.updateCustomAlert(a)));
          await refetchAlerts();
        } catch {
          toast.error('Failed to update alerts');
        }
      },
    }));

    if (!alertsEnabled) {
      return null;
    }

    return (
      <>
        <hr />
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <div className="flex flex-row justify-between items-center gap-4">
              <h2 className="text-ow-primary text-xl font-bold">Custom Alerts</h2>
              {!loading && <ToggleSwitch checked={customAlertsEnabled} onChange={handleCustomAlertsEnabledChange} />}
            </div>
            Define specific score changes that should trigger alerts.
          </div>

          {loading && <Loading />}

          {!loading && customAlertsEnabled && (
            <div className="flex flex-col gap-2">
              {!isCreating && (
                <button
                  onClick={() => {
                    setIsCreating(true);
                    setEditingAlertId(null);
                  }}
                  className="bg-ow-blue text-white font-medium px-6 py-2 rounded w-fit hover:opacity-90 transition-opacity"
                >
                  Create New Custom Alert
                </button>
              )}

              {isCreating && (
                <div className="border border-gray-200 rounded p-4">
                  <CustomAlertEditor
                    mode="create"
                    createDefaultName={createDefaultName}
                    onSave={handleCreateAlert}
                    onCancel={() => setIsCreating(false)}
                  />
                </div>
              )}

              {sortedAlerts.map((alert) => (
                <div key={alert.alert_id} data-custom-alert-card="true">
                  <hr />
                  <CustomAlertSummaryCard
                    alert={alert}
                    isEditing={editingAlertId === alert.alert_id}
                    onEdit={handleEditAlert}
                    onDelete={handleDeleteAlert}
                    onToggle={handleToggleAlert}
                    onUpdate={handleUpdateAlert}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  }
);

export default CustomAlertsSettings;
